import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, ChefHat, ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import FinalCTA from "@/components/sections/FinalCTA";
import { MENU, SPECIALS, LEGEND } from "@/lib/menu-data";
import { BUSINESS } from "@/lib/business-info";

const HERO_BG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/d3xkj1b7_0DA51A50-AC77-4548-B20A-3E1870B8DFBD.PNG";

const FEATURE_IMAGES = {
    entrees:
        "https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/wfwhbttq_20E119FE-48F8-4837-A259-B87021DF969B.PNG",
    seafood:
        "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/7b2tx0qd_6AED80A2-E5AD-4A8F-A910-849ACAE56061%202.PNG",
    vegan:
        "https://static.prod-images.emergentagent.com/jobs/c2b7593f-ae8f-4892-a9ba-b424e7885b58/images/bb2057e7db338c750b2b93cb1afe76d4f202726fe38b41fa33fe63ac126a855a.png",
};

// ---------- Small subcomponents ----------

function Eyebrow({ children, className = "" }) {
    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <span className="h-px w-8 bg-gold" />
            <span className="empire-eyebrow !text-gold !text-[10px]">{children}</span>
        </div>
    );
}

function DishLine({ item }) {
    return (
        <li
            className="group flex items-baseline gap-4 py-3 border-b border-cream/[0.06] last:border-b-0"
            data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-x-2">
                    <h4 className="font-serif text-cream text-[19px] md:text-[22px] leading-tight tracking-tight transition-colors duration-300 group-hover:text-gold">
                        {item.name}
                    </h4>
                    <div className="flex items-center gap-1.5">
                        {item.vegan && (
                            <span
                                title="Vegan"
                                aria-label="Vegan"
                                className="inline-flex items-center justify-center h-4 w-4 border border-gold/40 text-gold"
                            >
                                <Leaf size={9} strokeWidth={1.6} />
                            </span>
                        )}
                        {item.gf && (
                            <span
                                title="Gluten-Free"
                                className="inline-flex items-center justify-center px-1.5 h-4 border border-gold/40 text-gold text-[8px] font-medium tracking-[0.18em] uppercase"
                            >
                                GF
                            </span>
                        )}
                        {item.featured && (
                            <span className="text-gold text-[9px] uppercase tracking-[0.24em] ml-1">
                                · Signature
                            </span>
                        )}
                    </div>
                </div>
                {item.desc && (
                    <p className="text-cream/55 text-[13px] md:text-[14px] font-light leading-relaxed tracking-wide mt-2 max-w-prose">
                        {item.desc}
                    </p>
                )}
                {item.meta && (
                    <p className="text-cream/45 text-[12px] md:text-[13px] font-light italic tracking-wide mt-1.5">
                        {item.meta}
                    </p>
                )}
            </div>
            {/* Leader dots, hidden on small */}
            <span
                aria-hidden
                className="hidden md:block flex-1 border-b border-dotted border-cream/15 translate-y-[-6px] min-w-[40px]"
            />
            <span className="font-serif text-gold text-[17px] md:text-[19px] tracking-tight whitespace-nowrap">
                {item.price}
            </span>
        </li>
    );
}

function MenuSection({ section, isAlt }) {
    const img = FEATURE_IMAGES[section.id];
    return (
        <section
            id={`menu-${section.id}`}
            data-testid={`menu-section-${section.id}`}
            className={`empire-section ${isAlt ? "bg-charcoal-800" : "bg-charcoal"}`}
            aria-label={section.title}
        >
            <div className="empire-container">
                <div
                    className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start ${
                        isAlt ? "lg:flex-row-reverse" : ""
                    }`}
                >
                    {/* Heading column */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-4 lg:sticky lg:top-36"
                    >
                        <Eyebrow>{section.eyebrow}</Eyebrow>
                        <h2 className="mt-5 font-serif text-cream text-[2.4rem] md:text-[3rem] leading-[1.04] tracking-[-0.01em]">
                            {section.title}
                            <span className="text-gold">.</span>
                        </h2>
                        {section.note && (
                            <p className="mt-5 text-cream/65 text-[14px] md:text-[15px] font-light leading-[1.7] max-w-sm">
                                {section.note}
                            </p>
                        )}

                        {img && (
                            <div
                                className="relative mt-10 hidden lg:block aspect-[4/5] overflow-hidden"
                                data-testid={`menu-section-image-${section.id}`}
                            >
                                <img
                                    src={img}
                                    alt={`${section.title} at Empire Jamaica Fusion`}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-[1.05]"
                                />
                                <div className="absolute inset-0 bg-charcoal/25" />
                                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-transparent" />
                                <div className="absolute bottom-5 left-5 right-5">
                                    <Eyebrow>Featured</Eyebrow>
                                </div>
                            </div>
                        )}
                    </motion.div>

                    {/* Items column */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-8 space-y-12"
                    >
                        {section.groups.map((group, gi) => (
                            <div key={gi}>
                                {group.heading && (
                                    <div className="flex items-center gap-4 mb-6">
                                        <h3 className="font-serif italic text-gold/85 text-[22px] md:text-[26px] tracking-tight">
                                            {group.heading}
                                        </h3>
                                        <span className="flex-1 h-px bg-cream/[0.08]" />
                                    </div>
                                )}
                                {group.items.length > 0 ? (
                                    <ul className="border-t border-cream/[0.06]">
                                        {group.items.map((item, ii) => (
                                            <DishLine key={ii} item={item} />
                                        ))}
                                    </ul>
                                ) : group.placeholder ? (
                                    <p
                                        data-testid={`menu-placeholder-${gi}`}
                                        className="border-t border-cream/[0.06] pt-6 text-cream/55 font-light italic text-[14px] md:text-[15px] tracking-wide"
                                    >
                                        {group.placeholder}
                                    </p>
                                ) : null}
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

// Sticky tab nav for fast section jumping
function SectionNav({ active }) {
    return (
        <nav
            data-testid="menu-section-nav"
            className="sticky top-24 md:top-32 z-30 bg-charcoal/85 backdrop-blur-md border-y border-cream/10"
            aria-label="Menu sections"
        >
            <div className="empire-container">
                <ul className="flex items-center gap-1 md:gap-2 overflow-x-auto no-scrollbar py-3 -mx-1 px-1">
                    {MENU.map((s) => (
                        <li key={s.id}>
                            <a
                                href={`#menu-${s.id}`}
                                data-testid={`menu-jump-${s.id}`}
                                className={`whitespace-nowrap inline-block px-4 md:px-5 py-2 text-[10px] md:text-[11px] uppercase tracking-[0.22em] font-medium transition-colors border ${
                                    active === s.id
                                        ? "border-gold text-gold"
                                        : "border-transparent text-cream/65 hover:text-gold"
                                }`}
                            >
                                {s.title}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </nav>
    );
}

// ---------- Page ----------

export default function Menu() {
    const [active, setActive] = useState(MENU[0].id);

    useEffect(() => {
        document.title = "Menu · Empire Jamaica Fusion · Poughkeepsie, NY";
        window.scrollTo({ top: 0, behavior: "auto" });

        const onScroll = () => {
            const offset = 240;
            let current = MENU[0].id;
            for (const s of MENU) {
                const el = document.getElementById(`menu-${s.id}`);
                if (el && el.getBoundingClientRect().top - offset <= 0) {
                    current = s.id;
                }
            }
            setActive(current);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <Nav />
            <main data-testid="menu-page">
                {/* ---------- Hero ---------- */}
                <section
                    className="relative overflow-hidden bg-charcoal"
                    aria-label="Menu intro"
                    data-testid="menu-hero"
                >
                    <div aria-hidden className="absolute inset-0 z-0">
                        <img
                            src={HERO_BG}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover opacity-60"
                            style={{ filter: "contrast(1.08) saturate(1.05) brightness(0.98)" }}
                        />
                        <div className="absolute inset-0 bg-charcoal/45" />
                        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/55 to-charcoal/15" />
                        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/65 via-transparent to-charcoal" />
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_60%,rgba(212,175,55,0.10),rgba(0,0,0,0)_55%)]" />
                    </div>

                    <div className="empire-container relative z-10 pt-40 md:pt-52 pb-20 md:pb-28">
                        <Eyebrow>The Empire Table</Eyebrow>
                        <motion.h1
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="menu-heading"
                            className="mt-8 max-w-4xl font-serif text-cream text-[3rem] md:text-[5rem] lg:text-[6rem] leading-[1] tracking-[-0.01em]"
                        >
                            The <em className="italic font-normal text-gold">Menu</em>.
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.15 }}
                            className="mt-8 max-w-2xl text-cream/75 text-[16px] md:text-[18px] font-light leading-[1.7]"
                        >
                            Authentic Jamaican fusion — open-flame jerk, slow-braised
                            oxtail, coconut-rich vegan plates and coastal seafood.
                            Every dish made by hand, plated with intention, served on
                            Main Street, Poughkeepsie.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.3 }}
                            className="mt-12 flex flex-col sm:flex-row gap-4"
                        >
                            <Link
                                to="/#catering-inquiry"
                                data-testid="menu-cta-catering"
                                className="empire-btn-primary"
                            >
                                Book Catering
                                <ArrowRight size={14} />
                            </Link>
                            <a
                                href={BUSINESS.phoneLink}
                                data-testid="menu-cta-call"
                                className="empire-btn-secondary"
                            >
                                Call {BUSINESS.phoneDisplay}
                            </a>
                        </motion.div>
                    </div>
                </section>

                {/* ---------- Specials Banner ---------- */}
                <section
                    aria-label="Lunch specials"
                    data-testid="menu-specials"
                    className="bg-charcoal border-y border-cream/10"
                >
                    <div className="empire-container py-14 md:py-20">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
                            <div className="md:col-span-4">
                                <Eyebrow>Mid-Day Ritual</Eyebrow>
                                <h2 className="mt-5 font-serif text-cream text-[2rem] md:text-[2.5rem] leading-[1.04] tracking-tight">
                                    Lunch, <span className="italic text-gold">elevated.</span>
                                </h2>
                                <p className="mt-5 text-cream/65 text-[14px] font-light leading-[1.7] max-w-sm">
                                    Monday through Friday, 11AM – 2PM. Two prix-fixe
                                    plates that anchor the working day.
                                </p>
                            </div>

                            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-cream/10">
                                {[SPECIALS.lunch, SPECIALS.vegan].map((sp, i) => (
                                    <div
                                        key={i}
                                        data-testid={`special-card-${i}`}
                                        className="relative p-8 md:p-10 bg-burgundy-800/40 hover:bg-burgundy-800/60 transition-colors duration-500 group"
                                    >
                                        <span className="empire-eyebrow !text-gold !text-[10px]">
                                            {sp.eyebrow}
                                        </span>
                                        <h3 className="mt-4 font-serif text-cream text-[1.8rem] md:text-[2.1rem] leading-tight tracking-tight">
                                            {sp.title}
                                        </h3>
                                        <div className="mt-5 font-serif text-gold text-[2rem] md:text-[2.6rem] tracking-tight">
                                            {sp.price}
                                        </div>
                                        <p className="mt-3 text-cream/60 text-[13px] font-light italic">
                                            {sp.note}
                                        </p>
                                        {sp === SPECIALS.vegan && (
                                            <Leaf
                                                size={18}
                                                className="absolute top-8 right-8 text-gold/70"
                                                strokeWidth={1.4}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ---------- Section quick-nav ---------- */}
                <SectionNav active={active} />

                {/* ---------- Menu sections ---------- */}
                {MENU.map((section, i) => (
                    <MenuSection key={section.id} section={section} isAlt={i % 2 === 1} />
                ))}

                {/* ---------- Legend ---------- */}
                <section
                    aria-label="Dietary legend"
                    data-testid="menu-legend"
                    className="bg-charcoal border-t border-cream/10"
                >
                    <div className="empire-container py-20 md:py-28">
                        <div className="max-w-2xl">
                            <Eyebrow>Reading The Menu</Eyebrow>
                            <h2 className="mt-5 font-serif text-cream text-[2rem] md:text-[2.6rem] leading-[1.04] tracking-tight">
                                A quick legend.
                            </h2>
                            <ul className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-6">
                                {LEGEND.map((l) => (
                                    <li
                                        key={l.key}
                                        className="flex items-baseline gap-4"
                                        data-testid={`legend-${l.key.toLowerCase()}`}
                                    >
                                        <span className="font-serif text-gold text-3xl tracking-tight w-10">
                                            {l.key}
                                        </span>
                                        <span className="text-cream/80 text-[14px] tracking-wide">
                                            {l.label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* ---------- Mission pull quote ---------- */}
                <section
                    aria-label="Our mission"
                    data-testid="menu-mission"
                    className="bg-charcoal-800"
                >
                    <div className="empire-container py-24 md:py-36 text-center max-w-4xl mx-auto">
                        <ChefHat
                            size={28}
                            strokeWidth={1.2}
                            className="text-gold mx-auto"
                        />
                        <p className="mt-10 font-serif italic text-cream/95 text-[1.7rem] md:text-[2.4rem] lg:text-[2.8rem] leading-[1.25] tracking-tight">
                            “Michelle hopes to build community in Poughkeepsie through
                            Empire Jamaica Fusion. With its homey, warm environment, our
                            unique blend of flavors captivates the palate — embodying
                            the motto of Jamaica:{" "}
                            <span className="text-gold not-italic">out of many, one people.</span>”
                        </p>
                        <div className="mt-12 inline-flex items-center gap-4">
                            <span className="h-px w-12 bg-gold/60" />
                            <span className="empire-eyebrow !text-cream/55">
                                Empire Jamaica Fusion · Poughkeepsie
                            </span>
                            <span className="h-px w-12 bg-gold/60" />
                        </div>
                    </div>
                </section>

                {/* ---------- Order / Delivery rail ---------- */}
                <section
                    aria-label="Delivery partners"
                    data-testid="menu-delivery"
                    className="bg-charcoal border-y border-cream/10"
                >
                    <div className="empire-container py-14 md:py-20">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                            <div>
                                <Eyebrow>Order In</Eyebrow>
                                <h2 className="mt-5 font-serif text-cream text-[2rem] md:text-[2.6rem] leading-[1.04] tracking-tight">
                                    Empire, <span className="italic text-gold">delivered.</span>
                                </h2>
                            </div>
                            <p className="text-cream/55 text-[13px] font-light max-w-md">
                                Pickup, curbside or doorstep via our delivery partners.
                                Catering & private dining handled directly by our team.
                            </p>
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-cream/10">
                            {[
                                { name: "Uber Eats", tagline: "Fast Doorstep Delivery" },
                                { name: "DoorDash", tagline: "Citywide Coverage" },
                                { name: "Grubhub", tagline: "Office & Home Orders" },
                            ].map((d) => {
                                const link =
                                    BUSINESS.delivery.find((x) => x.name === d.name)?.url || "#";
                                const id = d.name.toLowerCase().replace(/\s+/g, "-");
                                return (
                                    <li key={d.name}>
                                        <a
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`menu-delivery-${id}`}
                                            aria-disabled="true"
                                            className="group flex items-center justify-between p-7 md:p-8 bg-charcoal-800 hover:bg-charcoal-700 transition-colors duration-500"
                                        >
                                            <div className="min-w-0">
                                                <span className="font-serif text-cream text-2xl md:text-3xl tracking-tight group-hover:text-gold transition-colors block">
                                                    {d.name}
                                                </span>
                                                <span className="mt-2 block text-cream/55 text-[12px] uppercase tracking-[0.22em] font-light">
                                                    {d.tagline}
                                                </span>
                                                <span
                                                    className="mt-4 inline-flex items-center gap-2 text-gold text-[10px] uppercase tracking-[0.26em] font-medium"
                                                    data-testid={`menu-delivery-coming-soon-${id}`}
                                                >
                                                    <span className="inline-block h-1 w-1 rounded-full bg-gold/80" />
                                                    Coming Soon
                                                </span>
                                            </div>
                                            <ArrowUpRight
                                                size={20}
                                                className="text-cream/40 group-hover:text-gold transition-colors shrink-0 ml-6"
                                            />
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </section>

                {/* Re-use the homepage Final CTA banner so the page closes on conversion */}
                <FinalCTA />
            </main>
            <Footer />
            <MobileStickyCTA />
        </>
    );
}
